import React from "react";
import { Helmet } from "react-helmet-async";
import { CheckCircle2 } from "lucide-react";

import Logo from "@/components/brand/Logo";
import Button from "@/components/foundation/Button";
import GlowOrb from "@/components/foundation/GlowOrb";

/**
 * BankConnectSuccess — utility fallback landing surface for the mobile app's
 * bank-connection deep link. When the ZenMe app is installed, the OS-level
 * Universal Link / App Link interception opens the app before this page ever
 * renders. This page is the graceful web fallback for users who don't have
 * the app installed or whose OS didn't intercept the link.
 *
 * Intentionally minimal — no navbar, no footer, no marketing.
 */
export default function BankConnectSuccess() {
  const reopenAppLink = () => {
    // Re-navigate to the canonical universal-link URL. On iOS/Android this
    // re-triggers the OS-level app handoff if the app is installed and
    // properly associated via the .well-known/ files.
    window.location.href = "https://zenmeonline.app/bank-connect-success";
  };

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-bg-base px-6 py-16 text-white"
      data-testid="bank-connect-success-page"
    >
      <Helmet>
        <title>Bank Connection Complete · ZenMe Capital</title>
        <meta name="robots" content="noindex,nofollow" />
        <link
          rel="canonical"
          href="https://zenmeonline.app/bank-connect-success"
        />
      </Helmet>

      <GlowOrb
        color="emerald"
        size={620}
        intensity={0.18}
        className="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        animate={false}
      />

      <div className="relative z-10 mx-auto flex w-full max-w-md flex-col items-center gap-8 text-center">
        {/* Logo */}
        <Logo size="md" />

        {/* Success glyph */}
        <div className="relative flex h-16 w-16 items-center justify-center rounded-full border border-brand-emerald/30 bg-brand-emerald/10 text-brand-emerald">
          <CheckCircle2 className="h-8 w-8" strokeWidth={1.75} />
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full shadow-[0_0_28px_rgba(0,230,118,0.35)]"
          />
        </div>

        {/* Eyebrow */}
        <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-emerald">
          Bank Connection Complete
        </span>

        {/* Headline */}
        <h1 className="font-display text-3xl font-semibold leading-[1.1] tracking-tight text-white sm:text-4xl">
          Your bank account is connected.
        </h1>

        {/* Body */}
        <p className="max-w-sm text-sm leading-relaxed text-text-secondary sm:text-base">
          Return to the ZenMe app to continue your application.
        </p>

        {/* CTA */}
        <Button
          variant="primary"
          size="lg"
          onClick={reopenAppLink}
          data-testid="open-zenme-button"
        >
          Open ZenMe
        </Button>

        {/* Footnote */}
        <p className="mt-2 text-xs text-text-muted">
          You can safely close this window and return to the ZenMe app.
        </p>
      </div>
    </div>
  );
}
